'use client';

/**
 * AI バグ報告整形フック
 *
 * 簡素化バグ報告入力を AI で整形し、結果を管理する。
 * - 整形リクエスト送信
 * - タイムアウト/エラーハンドリング
 * - 類似 Issue 検索
 * - フォールバック（整形なし投稿）
 */

import { useCallback, useState } from 'react';

import type { AiFormatResult, SimilarIssue, SimplifiedBugReportInput } from '@/lib/debug/types';

export type AiFormatStatus = 'idle' | 'formatting' | 'done' | 'error';

interface UseAiFormatReturn {
  status: AiFormatStatus;
  result: AiFormatResult | null;
  similarIssues: SimilarIssue[];
  error: string | null;
  /** AI 整形をリクエスト */
  requestFormat: (input: SimplifiedBugReportInput) => Promise<void>;
  /** 整形結果のタイトルを編集 */
  updateTitle: (title: string) => void;
  /** 整形結果のセクション内容を編集 */
  updateSection: (index: number, content: string) => void;
  /** 整形結果をリセットして入力画面に戻す */
  reset: () => void;
}

export function useAiFormat(): UseAiFormatReturn {
  const [status, setStatus] = useState<AiFormatStatus>('idle');
  const [result, setResult] = useState<AiFormatResult | null>(null);
  const [similarIssues, setSimilarIssues] = useState<SimilarIssue[]>([]);
  const [error, setError] = useState<string | null>(null);

  const requestFormat = useCallback(async (input: SimplifiedBugReportInput) => {
    setStatus('formatting');
    setError(null);
    setSimilarIssues([]);

    try {
      // 類似 Issue 検索を非ブロッキングで開始（失敗しても無視）
      void fetch('/api/debug/issue/similar', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query: input.complaint }),
        signal: AbortSignal.timeout(10_000),
      })
        .then(async (res) => {
          if (!res.ok) return;
          const similarData = (await res.json()) as { similarIssues: SimilarIssue[] };
          setSimilarIssues(similarData.similarIssues ?? []);
        })
        .catch(() => {
          // 類似検索失敗は無視
        });

      // AI 整形リクエスト（ユーザー入力のみ送信、autoContext は送らない）
      const response = await fetch('/api/debug/issue/format', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(input),
        signal: AbortSignal.timeout(30_000),
      });
      if (!response.ok) {
        const errorData = (await response.json().catch(() => ({ error: 'AI 整形に失敗しました' }))) as {
          error?: string;
        };
        throw new Error(errorData.error || `AI 整形に失敗しました (${response.status})`);
      }

      const responseData = (await response.json()) as { success?: boolean; data?: AiFormatResult; error?: string };
      const rawResult = responseData.data ?? (responseData as unknown as AiFormatResult);
      // suggestions が返らない場合は空配列をセット
      const formatResult: AiFormatResult = { ...rawResult, suggestions: [] };

      setResult(formatResult);
      setStatus('done');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'AI 整形に失敗しました';
      const isTimeout = message.includes('timeout') || message.includes('abort');
      setError(isTimeout ? 'AI 整形がタイムアウトしました' : message);
      setStatus('error');
    }
  }, []);

  const updateTitle = useCallback((title: string) => {
    setResult((prev) => {
      if (!prev) return prev;
      return { ...prev, title };
    });
  }, []);

  const updateSection = useCallback((index: number, content: string) => {
    setResult((prev) => {
      if (!prev) return prev;
      const sections = [...prev.sections];
      sections[index] = { ...sections[index], content };
      return { ...prev, sections };
    });
  }, []);

  const reset = useCallback(() => {
    setStatus('idle');
    setResult(null);
    setSimilarIssues([]);
    setError(null);
  }, []);

  return {
    status,
    result,
    similarIssues,
    error,
    requestFormat,
    updateTitle,
    updateSection,
    reset,
  };
}
